import { pxToRem } from "@/util";
import {
  Box,
  Button,
  Checkbox,
  TextField,
  Typography,
  FormControlLabel,
} from "@mui/material";
import { makeStyles } from "tss-react/mui";
import { useFormik } from "formik";
import {
  consultationSchema,
  // consultationInitialValues,
} from "../../util/validationSchema/consultation";
import Select2 from "../global/select2";
import Date_TimePicker from "../global/datetime";
import { createConsultationApi } from "@/util/api/consultations";
import { Dayjs } from "dayjs";
import { useMutation } from "@tanstack/react-query";
import useToastAlert from "@/util/hooks/usesnackbar";
import { AxiosError } from "axios";
import {
  ICreateConsultationPayload,
  consultationType,
  consultationStatus,
} from "@/util/types/consultation";
import Link from "next/link";

// Corrected initialValues:
const consultationInitialValues = {
  email: "",
  firstName: "",
  lastName: "",
  phone: "",
  message: "",
  type: consultationType.PHYSICAL,
  preferredDate: null as Dayjs | null,
  agreed: false,
};

export default function InputForm() {
  const { classes } = styles();
  const toastAlert = useToastAlert();

  const createConsultationMutation = useMutation({
    mutationFn: createConsultationApi,
    onSuccess: () => {
      toastAlert({
        message: "Consultation created successfully",
        type: "success",
      });
      formik.resetForm();
    },
    onError: (error: AxiosError) => {
      console.log("Error creating consultation:", error);
      let msg:string="";
   
  

  const message = (error.response?.data as { message?: string })?.message;

  if (typeof message === "string" && message.includes("dup key:")) {
    console.log("duplicate found!");
    msg = "Date and time is already booked. Please choose another slot.";
  } else {
    msg = error.message;
  }

      toastAlert({
        message: msg,
        type: "error",
      });
    },
  });

  const formik = useFormik({
    initialValues: consultationInitialValues,
    validationSchema: consultationSchema,
    onSubmit: async (values) => {
      let dateObj: Date | undefined;
      let timeString = "";

      if (values.preferredDate instanceof Date) {
        dateObj = values.preferredDate;
      } else if (values.preferredDate && "toDate" in values.preferredDate) {
        dateObj = values.preferredDate.toDate();
      }

      if (dateObj) {
        timeString = dateObj.toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        });
      }

      const payload: Partial<ICreateConsultationPayload> = {
        email: values.email,
        firstName: values.firstName,
        lastName: values.lastName,
        phone: values.phone,
        message: values.message,
        type: values.type,
        date: dateObj,
        time: timeString,
        consultationStatus: consultationStatus.PENDING,
      };

      createConsultationMutation.mutate(payload);
    },
  });

  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    handleSubmit,
    setFieldValue,
  } = formik;

  const handleDateChange = (date: Dayjs | null) => {
    setFieldValue("preferredDate", date);
  };

  return (
    <form onSubmit={handleSubmit}>
      <Box className={classes.container}>
        {/* Basic Details */}
        <Box className={classes.form1}>
          {["firstName", "lastName", "email", "phone"].map((field) => (
            <Box key={field} className={classes.input}>
              <Typography className={classes.label}>
                {field
                  .replace(/([A-Z])/g, " $1")
                  .replace(/^./, (s) => s.toUpperCase())}
              </Typography>
              <TextField
                name={field}
                placeholder={field
                  .replace(/([A-Z])/g, " $1")
                  .replace(/^./, (s) => s.toUpperCase())}
                value={`${values[field as keyof typeof values]}`}
                onChange={handleChange}
                onBlur={handleBlur}
                error={
                  touched[field as keyof typeof values] &&
                  !!errors[field as keyof typeof values]
                }
                helperText={
                  touched[field as keyof typeof values] &&
                  errors[field as keyof typeof values]
                }
              />
            </Box>
          ))}
        </Box>

        {/* Consultation Type & Date */}
        <Box className={classes.form2}>
          <Box className={classes.input}>
            <Typography className={classes.label}>Consultation Type</Typography>
            <Select2
              value={values.type}
              onChange={(val) => setFieldValue("type", val)}
              error={touched.type && !!errors.type}
            />
          </Box>
          <Box className={classes.input}>
            <Typography className={classes.label}>
              Preferred DateTime
            </Typography>
            <Date_TimePicker
              value={values?.preferredDate}
              onChange={handleDateChange}
            />
          </Box>
        </Box>

        {/* Additional Info */}
        <Box className={classes.form3}>
          <Box className={classes.input}>
            <Typography className={classes.label}>
              Additional Information
            </Typography>
            <TextField
              name="message"
              placeholder="Info"
              multiline
              rows={5}
              variant="outlined"
              fullWidth
              value={values.message}
              onChange={handleChange}
              onBlur={handleBlur}
            />
          </Box>
        </Box>

        {/* Agreement & Submit */}
        <Box className={classes.form4}>
          <Box className={classes.check}>
            <FormControlLabel
              control={
                <Checkbox
                  name="agreed"
                  checked={values.agreed}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
              }
              label={
                <Typography>
                  I agree with{" "}
                  <Link
                    href="/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className={classes.link}
                    prefetch={true}
                  >
                    Arel Privacy Policy
                  </Link>
                </Typography>
              }
            />
          </Box>
          {touched.agreed && errors.agreed && (
            <Typography color="error">{errors.agreed}</Typography>
          )}
          <Button
            type="submit"
            variant="contained"
            className={classes.btn}
            disabled={
              formik.isSubmitting || createConsultationMutation.isPending
            }
          >
            {createConsultationMutation.isPending
              ? "Submitting..."
              : "Send a message"}
          </Button>
        </Box>
      </Box>
    </form>
  );
}

// Styles
const styles = makeStyles()((theme) => ({
  container: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
    height: "100%",
  },
  link: {
    color: "#1A35DF",
  },
  input: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
  },
  label: {
    fontWeight: 500,
    fontSize: pxToRem(14),
  },
  form1: {
    display: "grid",
    gridTemplateColumns: "repeat(2,1fr)",
    gap: theme.spacing(4),
    [theme.breakpoints.down("sm")]: {
      display: "flex",
      flexDirection: "column",
    },
  },
  form2: {
    display: "grid",
    gridTemplateColumns: "2fr 2fr",
    gap: theme.spacing(4),
    marginTop: theme.spacing(4),
    [theme.breakpoints.down("sm")]: {
      display: "flex",
      flexDirection: "column",
    },
  },
  form3: {
    marginTop: theme.spacing(4),
  },
  form4: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    marginTop: theme.spacing(4),
  },
  check: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
  },
  btn: {
    width: pxToRem(170),
    height: pxToRem(46),
  },
}));
